import { useCallback } from 'react'
import { getSingleShapeRotationGuideCenter, snapRotationAngle } from '@/lib/guides/smart-guides'
import { shortestDeltaDegrees } from '@/lib/transforms/geometry'
import {
  buildMultiRotateInitials,
  collectChangedMultiRotateCommits,
  computeMultiRotatePreview,
} from '@/lib/transforms/multi-rotation'
import { isShapeSelectable } from '../helpers/selection-scope'
import { computeMultiSelectChrome, roundRotationDegrees } from '../helpers/multi-select-geometry'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { EditScopeRefsApi } from './use-edit-scope-refs'
import type { SnapRefsApi } from './use-snap-refs'
import type { ShapeUpdatesApi } from './use-shape-updates'
import type { SmartGuidesApi } from './use-smart-guides'

export function usePanelMultiSelectRotation(
  ctx: ShapeManagerStateApi & EditScopeRefsApi & SnapRefsApi & ShapeUpdatesApi & SmartGuidesApi
) {
  const {
    editScopeGroupIdRef,
    emitSmartGuides,
    multiSelectChromeRef,
    panelMultiRotateBaselineRef,
    pendingRotateChromeBeforeRef,
    selectedShapeIdsRef,
    setMultiSelectChrome,
    shapesRef,
    smartGuidesEnabledRef,
    updateShapeRef,
  } = ctx

  const beginPanelMultiSelectRotation = useCallback(() => {
    const ids = selectedShapeIdsRef.current

    if (ids.length < 2) return

    const list = shapesRef.current
    const prevChrome = multiSelectChromeRef.current
    const prevBoxRot = roundRotationDegrees(prevChrome?.rotationDeg ?? 0)

    // `computeMultiSelectChrome` is a canvas-axis AABB. Pairing that geometry with a non-zero
    // `rotationDeg` (CSS rotate on the overlay) is invalid — same bug as recomputing each slider
    // tick. When the box is already turned, keep pivot/size from the existing OBB; only refresh
    // from union when the displayed angle is 0° (canonical frame).
    let nextChrome: MultiSelectChrome | null = null

    if (prevChrome != null && prevBoxRot !== 0) {
      nextChrome = { ...prevChrome, rotationDeg: prevBoxRot }
    } else {
      const geo = computeMultiSelectChrome(ids, list)

      if (geo) {
        nextChrome = { ...geo, rotationDeg: prevBoxRot }
      }
    }

    if (!nextChrome) {
      return
    }

    setMultiSelectChrome(nextChrome)
    // `multiSelectChromeRef` is normally synced from render state; object-panel commits read the
    // snapshot in the same tick as these setState calls (before paint). Keep the ref aligned
    // with mixed-selection `unifiedRotateChromeForHistoryRef` so undo/redo history captures the
    // true post-rotate chrome for `multiSelectChromeAfter`.
    multiSelectChromeRef.current = nextChrome

    const selected = list.filter(
      (s) => ids.includes(s.id) && isShapeSelectable(s, editScopeGroupIdRef.current)
    )

    if (selected.length < 2) return

    const selectionKey = [...ids].sort().join('|')

    panelMultiRotateBaselineRef.current = {
      selectionKey,
      initials: buildMultiRotateInitials(
        selected.map((shape) => ({
          id: shape.id,
          x: shape.x,
          y: shape.y,
          width: shape.width,
          height: shape.height,
          rotation: shape.rotation ?? 0,
        }))
      ),
      startChrome: { ...nextChrome },
      startChromeDeg: prevBoxRot,
      pivot: { x: nextChrome.pivotX, y: nextChrome.pivotY },
    }
    pendingRotateChromeBeforeRef.current = { ...nextChrome }
  }, [
    editScopeGroupIdRef,
    multiSelectChromeRef,
    panelMultiRotateBaselineRef,
    pendingRotateChromeBeforeRef,
    selectedShapeIdsRef,
    setMultiSelectChrome,
    shapesRef,
  ])

  const applyPanelMultiSelectRotationPreview = useCallback(
    (targetDegrees: number): number | null => {
      let b = panelMultiRotateBaselineRef.current

      if (!b) {
        beginPanelMultiSelectRotation()
        b = panelMultiRotateBaselineRef.current
      }

      if (!b) return null

      const ids = selectedShapeIdsRef.current

      if (ids.length < 2) return null

      const selectionKey = [...ids].sort().join('|')

      if (selectionKey !== b.selectionKey) return null

      const targetRounded = roundRotationDegrees(targetDegrees)
      const deltaDeg = shortestDeltaDegrees(b.startChromeDeg, targetRounded)
      const updates = computeMultiRotatePreview({
        initials: b.initials,
        pivot: b.pivot,
        deltaDegrees: deltaDeg,
      })

      for (const [id, update] of Object.entries(updates)) {
        updateShapeRef.current(id, update)
      }

      // Match canvas handle: keep pivot/size fixed for this gesture; only advance rotation.
      // Recomputing union here is axis-aligned in canvas space while CSS also rotates the box,
      // which shrinks the frame and clips rotated corners.
      //
      // Update `multiSelectChromeRef` synchronously before returning: object-panel commits push
      // history in the same tick, and React may run this `setState` updater later. Deferred ref
      // writes produced `multiSelectChromeAfter` equal to pre-rotate chrome (redo one step behind).
      const prevChrome = multiSelectChromeRef.current

      if (!prevChrome) {
        return null
      }

      const merged = { ...prevChrome, rotationDeg: targetRounded }

      multiSelectChromeRef.current = merged
      setMultiSelectChrome(merged)

      return targetRounded
    },
    [
      beginPanelMultiSelectRotation,
      multiSelectChromeRef,
      panelMultiRotateBaselineRef,
      selectedShapeIdsRef,
      setMultiSelectChrome,
      updateShapeRef,
    ]
  )

  const finishPanelMultiSelectRotation = useCallback((): BatchShapeTransformItem[] | null => {
    const b = panelMultiRotateBaselineRef.current

    if (!b) return null
    panelMultiRotateBaselineRef.current = null

    const all = shapesRef.current
    const currentById = Object.fromEntries(
      all.map((shape) => [shape.id, { x: shape.x, y: shape.y, rotation: shape.rotation ?? 0 }])
    )
    const commits = collectChangedMultiRotateCommits({
      initials: b.initials,
      currentById,
    })
    const items: BatchShapeTransformItem[] = commits.map((item) => {
      const shape = all.find((s) => s.id === item.id)

      return {
        shapeId: item.id,
        shape: shape ? { ...shape } : undefined,
        before: item.before,
        after: item.after,
      }
    })

    if (items.length > 0) {
      pendingRotateChromeBeforeRef.current = { ...b.startChrome }
    }

    return items
  }, [panelMultiRotateBaselineRef, pendingRotateChromeBeforeRef, shapesRef])

  const getMultiSelectChromeRotationDeg = useCallback((): number | null => {
    if (selectedShapeIdsRef.current.length < 2) return null

    const c = multiSelectChromeRef.current

    return roundRotationDegrees(c?.rotationDeg ?? 0)
  }, [multiSelectChromeRef, selectedShapeIdsRef])

  const updatePanelRotationGuidePreview = useCallback(
    (rawDegrees: number | null) => {
      if (rawDegrees === null) {
        emitSmartGuides([])
        return
      }

      if (!smartGuidesEnabledRef.current) {
        emitSmartGuides([])
        return
      }

      const snap = snapRotationAngle(rawDegrees)

      if (!snap.didSnap) {
        emitSmartGuides([])
        return
      }

      const ids = selectedShapeIdsRef.current

      if (ids.length === 1) {
        const shape = shapesRef.current.find((s) => s.id === ids[0])

        if (!shape) {
          emitSmartGuides([])
          return
        }

        const { centerX, centerY } = getSingleShapeRotationGuideCenter(shape)

        emitSmartGuides([
          {
            type: 'rotation',
            position: snap.snapped,
            from: 0,
            to: 0,
            angle: snap.snapped,
            centerX,
            centerY,
          },
        ])
        return
      }

      if (ids.length > 1) {
        const c = multiSelectChromeRef.current

        if (!c) {
          emitSmartGuides([])
          return
        }

        emitSmartGuides([
          {
            type: 'rotation',
            position: snap.snapped,
            from: 0,
            to: 0,
            angle: snap.snapped,
            centerX: c.pivotX,
            centerY: c.pivotY,
          },
        ])
        return
      }

      emitSmartGuides([])
    },
    [emitSmartGuides, multiSelectChromeRef, selectedShapeIdsRef, shapesRef, smartGuidesEnabledRef]
  )

  return {
    beginPanelMultiSelectRotation,
    applyPanelMultiSelectRotationPreview,
    finishPanelMultiSelectRotation,
    getMultiSelectChromeRotationDeg,
    updatePanelRotationGuidePreview,
  }
}

export type PanelMultiSelectRotationApi = ReturnType<typeof usePanelMultiSelectRotation>
